class ProductGallery {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.mainGallery = container.querySelector('.product-atf__gallery-main');
    this.thumbsGallery = container.querySelector('.product-atf__gallery-thumbs');
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.swiperInitialized = false;

    if (!this.mainGallery || !this.thumbsGallery) return;

    this.onResize = this.debounce(this.onResize.bind(this), 250);
    this.checkViewportAndInitSlider();
    this.bindEvents();
  }

  /**
   * Checks current viewport width and initializes or destroys Swiper based on viewport
   */
  checkViewportAndInitSlider() {
    this.isMobile = window.innerWidth < 990;

    if (this.isMobile && !this.swiperInitialized) {
      this.initSwiper();
    } else if (!this.isMobile && this.swiperInitialized) {
      this.destroySwiper();
    }
  }

  /**
   * Initialize Swiper instances for main gallery and thumbnails
   */
  initSwiper() {
    if (this.swiperInitialized) return;

    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      console.error('Swiper not loaded');
      return;
    }

    // Create thumbs swiper first
    this.thumbsSwiper = new Swiper(this.thumbsGallery, {
      slidesPerView: 5,
      spaceBetween: 8,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      // threshold: 10,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous thumbnail',
        nextSlideMessage: 'Next thumbnail'
      }
    });

    // Initialize main gallery with thumbs as navigation
    this.mainSwiper = new Swiper(this.mainGallery, {
      slidesPerView: 1,
      spaceBetween: 0,
      thumbs: {
        swiper: this.thumbsSwiper
      },
      grabCursor: true,
      watchSlidesProgress: true,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous product image',
        nextSlideMessage: 'Next product image'
      }
    });

    this.swiperInitialized = true;
  }

  /**
   * Destroy Swiper instances when not needed
   */
  destroySwiper() {
    if (!this.swiperInitialized) return;

    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }

    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }

    this.swiperInitialized = false;
  }

  /**
   * Resize event handler
   */
  onResize() {
    this.checkViewportAndInitSlider();
  }

  /**
   * Create a debounce function for better performance
   * @param {Function} func - The function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @return {Function} - Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    window.addEventListener('resize', this.onResize);
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    window.removeEventListener('resize', this.onResize);
    this.destroySwiper();
  }

  /**
   * Method to be called when the section is updated in the customizer
   */
  onSectionUpdate() {
    this.destroySwiper();
    this.checkViewportAndInitSlider();
  }
}

/**
 * Initialize all product galleries on the page
 */
function initProductGalleries() {
  const galleries = document.querySelectorAll('.product-atf__gallery');
  return Array.from(galleries).map(gallery => new ProductGallery(gallery));
}

/**
 * Shopify Theme Editor event handlers
 */
document.addEventListener('DOMContentLoaded', () => {
  initProductGalleries();
});

document.addEventListener('shopify:section:load', (event) => {
  const galleries = event.target.querySelectorAll('.product-atf__gallery');
  if (galleries.length > 0) {
    galleries.forEach(gallery => new ProductGallery(gallery));
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  // When unloading, we can't access instances anymore
  // Just make sure all event listeners are cleaned up properly in the class destroy method
});

document.addEventListener('shopify:section:select', (event) => {
  // For selection events, we'll reinitialize the galleries
  // This might be less efficient than the registry approach but fulfills the requirement
  const galleries = event.target.querySelectorAll('.product-atf__gallery');
  if (galleries.length > 0) {
    galleries.forEach(gallery => {
      // Attempt to clean up any existing swiper instances
      const instance = new ProductGallery(gallery);
      instance.onSectionUpdate();
    });
  }
});


class ProductAccordions extends HTMLElement {
  constructor() {
    super();
    this.accordionTriggers = this.querySelectorAll('.accordion-trigger');
    this.init();
  }

  init() {
    // Initialize event listeners for each accordion trigger
    this.accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.toggleAccordion.bind(this, trigger));
    });

    // Initialize accessible keyboard navigation
    this.initAccessibility();
  }

  toggleAccordion(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const controlId = trigger.getAttribute('aria-controls');
    const content = document.getElementById(controlId);

    // Set aria and class attributes
    trigger.setAttribute('aria-expanded', !isExpanded);
    trigger.classList.toggle('active', !isExpanded);

    if (content) {
      if (!isExpanded) {
        // Opening animation
        content.hidden = false;
        content.classList.remove('hidden');
        content.classList.add('active');

        // Calculate the exact height needed
        const scrollHeight = content.scrollHeight;
        content.style.maxHeight = `${scrollHeight}px`;
      } else {
        // Closing animation - first set max-height to current height
        content.style.maxHeight = `${content.scrollHeight}px`;

        // Force a reflow to ensure the browser recognizes the current maxHeight
        void content.offsetWidth;

        // Now set it to 0 for the animation
        content.classList.remove('active');
        content.style.maxHeight = '0';

        // Wait for animation to complete before hiding completely
        setTimeout(() => {
          if (!content.classList.contains('active')) {
            content.classList.add('hidden');
            content.hidden = true;
          }
        }, 300); // Match this to your animation duration in CSS
      }
    }
  }

  closeAllAccordions() {
    this.accordionTriggers.forEach(trigger => {
      const controlId = trigger.getAttribute('aria-controls');
      const content = document.getElementById(controlId);

      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove('active');

      if (content) {
        content.classList.remove('active');
        content.classList.add('hidden');
        content.hidden = true;
        content.style.maxHeight = '0';
      }
    });
  }

  initAccessibility() {
    // Add keyboard navigation
    this.accordionTriggers.forEach(trigger => {
      trigger.addEventListener('keydown', (event) => {
        const key = event.key;

        if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          this.toggleAccordion(trigger);
        }
      });
    });
  }
}

// Register the custom element
if (!customElements.get('product-accordions')) {
  customElements.define('product-accordions', ProductAccordions);
}